"use client";
import {
    createContext,
    useContext,
    useEffect,
    useState,
    type ReactNode,
} from "react";
import { type Session } from "@/types/auth";
import { signOutLocal } from "@/utils/serverActions";

/**
 * Client-side session access, replacing next-auth/react. The provider fetches
 * the session once on mount, which is the behaviour the app relied on before;
 * no consumer reads a loading status or updates the session from the client.
 */
const SessionContext = createContext<{ data: Session | null }>({ data: null });

export function SessionProvider({
    children,
    initialSession,
}: {
    children: ReactNode;
    initialSession?: Session | null;
}) {
    // Seeded from the server render so consumers have the session at first
    // paint; the fetch below only covers client-side lifecycle changes.
    const [session, setSession] = useState<Session | null>(initialSession ?? null);

    useEffect(() => {
        let cancelled = false;
        const refresh = () => {
            fetch("/api/auth/session")
                .then((res) => {
                    // A non-ok response is a transient server problem, not a
                    // signed-out verdict; keep the current session. Only a
                    // valid 200 null flips the UI to signed out.
                    if (!res.ok) return undefined;
                    return res.json() as Promise<Session | null>;
                })
                .then((data) => {
                    if (!cancelled && data !== undefined) setSession(data);
                })
                .catch(() => {
                    // Session stays as is; server-side guards remain authoritative.
                });
        };
        refresh();
        // Revocations and role changes reach long-lived tabs when the user
        // returns to them, matching the previous provider's focus refetch.
        window.addEventListener("focus", refresh);
        return () => {
            cancelled = true;
            window.removeEventListener("focus", refresh);
        };
    }, []);

    return (
        <SessionContext.Provider value={{ data: session }}>
            {children}
        </SessionContext.Provider>
    );
}

export function useSession(): { data: Session | null } {
    return useContext(SessionContext);
}

export function signIn() {
    // Carry the page the user was trying to open through the login flow: a
    // returnTo already present (set by a server-side guard redirect) wins,
    // otherwise the current location is the destination.
    const params = new URLSearchParams(window.location.search);
    const current = window.location.pathname + window.location.search;
    const returnTo =
        params.get("returnTo") ?? (window.location.pathname === "/" ? null : current);
    window.location.assign(
        returnTo ? `/auth/signin?returnTo=${encodeURIComponent(returnTo)}` : "/auth/signin",
    );
}

export async function signOut() {
    // Hard navigation after the session is destroyed: a soft redirect would
    // keep the provider's session and the jotai atoms alive, leaving the
    // previous user's identity rendered on a signed-out page.
    await signOutLocal();
    window.location.assign("/auth/signin");
}
